import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CheckCircle2, AlertTriangle, ArrowRight, UserPlus, LogIn } from "lucide-react";
import { LogoMark } from "@/components/app/Brand";

const InviteAccept = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading, refresh } = useAuth();
  const [invite, setInvite] = useState(null);
  const [lookupError, setLookupError] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/team/invitations/lookup/${token}`);
        setInvite(data);
      } catch (e) {
        setLookupError(e?.response?.data?.detail || "Invitation not found");
      }
    })();
  }, [token]);

  const accept = async () => {
    setAccepting(true);
    try {
      await api.post(`/team/invitations/${token}/accept`);
      await refresh();
      setDone(true);
      toast.success(`You've joined ${invite?.workspace_owner || "the workspace"}`);
      setTimeout(() => navigate("/dashboard"), 1200);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to accept invitation");
    } finally {
      setAccepting(false);
    }
  };

  const goToAuth = (mode) => {
    const next = encodeURIComponent(`/invite/${token}`);
    navigate(`/auth?mode=${mode}&next=${next}&prefill=${encodeURIComponent(invite?.email || "")}`);
  };

  return (
    <div className="min-h-screen bg-[#0D1117] flex flex-col">
      <header className="border-b border-[#1F2937] px-8 py-5">
        <Link to="/" className="flex items-center gap-2.5 w-fit group" data-testid="invite-brand-link">
          <LogoMark size={28} />
          <span className="font-display font-black tracking-tighter text-lg leading-none group-hover:text-[#2A7BFF] transition-colors">
            Combined<span className="text-[#2A7BFF]">Work</span>
          </span>
        </Link>
      </header>

      <main className="flex-1 grid place-items-center px-6">
        <div className="w-full max-w-lg" data-testid="invite-card">
          {lookupError ? (
            <div className="border border-gray-900 p-8">
              <AlertTriangle className="w-8 h-8 text-amber-600" strokeWidth={1.5} />
              <div className="text-xs uppercase tracking-[0.25em] text-amber-700 mt-3">Invitation unavailable</div>
              <h1 className="font-display font-black text-3xl tracking-tighter mt-2">{lookupError}</h1>
              <p className="text-sm text-slate-400 mt-3">
                Ask the person who invited you to send a fresh link.
              </p>
              <Link to="/" className="inline-flex items-center mt-6 text-sm text-[#002FA7] font-semibold" data-testid="invite-back-home">
                Back to home <ArrowRight className="w-4 h-4 ml-1" />
              </Link>
            </div>
          ) : !invite ? (
            <div className="text-sm uppercase tracking-[0.25em] text-gray-400">Loading invitation…</div>
          ) : done ? (
            <div className="border border-gray-900 p-8" data-testid="invite-accepted-card">
              <CheckCircle2 className="w-8 h-8 text-[#002FA7]" strokeWidth={1.5} />
              <div className="text-xs uppercase tracking-[0.25em] text-[#002FA7] mt-3">Welcome aboard</div>
              <h1 className="font-display font-black text-3xl tracking-tighter mt-2">
                You&apos;re in.
              </h1>
              <p className="text-sm text-slate-400 mt-3">Taking you to the dashboard…</p>
            </div>
          ) : (
            <div className="border border-gray-900 p-8">
              <div className="text-xs uppercase tracking-[0.25em] text-[#002FA7]">Workspace invitation</div>
              <h1 className="font-display font-black text-4xl tracking-tighter mt-2 leading-tight">
                Join {invite.workspace_owner || "this workspace"}.
              </h1>
              <div className="mt-5 grid grid-cols-2 gap-px bg-[#1F2937]">
                <div className="bg-[#0D1117] p-4">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-gray-500">Invited as</div>
                  <div className="font-mono text-sm mt-1 truncate" data-testid="invite-email">{invite.email}</div>
                </div>
                <div className="bg-[#0D1117] p-4">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-gray-500">Role</div>
                  <div className="font-display font-bold text-sm uppercase mt-1" data-testid="invite-role">{invite.role}</div>
                </div>
              </div>

              {authLoading ? (
                <div className="mt-6 text-sm text-gray-500">Checking your session…</div>
              ) : !user ? (
                <div className="mt-6 space-y-3">
                  <p className="text-sm text-slate-400">
                    Sign in or create an account with <span className="font-mono">{invite.email}</span> to accept.
                  </p>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => goToAuth("signup")}
                      className="rounded-none flex-1 bg-[#002FA7] hover:bg-[#002480]"
                      data-testid="invite-signup-btn"
                    >
                      <UserPlus className="w-4 h-4 mr-1" /> Create account
                    </Button>
                    <Button
                      onClick={() => goToAuth("login")}
                      variant="outline"
                      className="rounded-none flex-1 border-gray-900"
                      data-testid="invite-login-btn"
                    >
                      <LogIn className="w-4 h-4 mr-1" /> Sign in
                    </Button>
                  </div>
                </div>
              ) : user.email?.toLowerCase() !== invite.email.toLowerCase() ? (
                <div className="mt-6 space-y-3">
                  <div className="flex items-start gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 px-3 py-2">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" strokeWidth={1.7} />
                    <span>
                      You&apos;re signed in as <span className="font-mono">{user.email}</span>, but this invite is for{" "}
                      <span className="font-mono">{invite.email}</span>.
                    </span>
                  </div>
                  <Button
                    onClick={() => goToAuth("login")}
                    variant="outline"
                    className="rounded-none w-full border-gray-900"
                    data-testid="invite-switch-account-btn"
                  >
                    Sign in as {invite.email}
                  </Button>
                </div>
              ) : (
                <Button
                  onClick={accept}
                  disabled={accepting}
                  className="rounded-none w-full mt-6 h-11 bg-[#002FA7] hover:bg-[#002480]"
                  data-testid="invite-accept-btn"
                >
                  {accepting ? "Joining…" : <>Accept &amp; join workspace <ArrowRight className="w-4 h-4 ml-1" /></>}
                </Button>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default InviteAccept;
